import { Link } from 'react-router-dom';
import type { HeroData, HeroButton } from './types';
import Banner from '../../../assets/images/banner.jpg';

interface HeroSectionProps {
  heroData: HeroData | null;
  isVisible: boolean;
  scrolled: boolean;
  onButtonClick: (button: HeroButton) => void;
}

export default function HeroSection({
  heroData,
  isVisible,
  scrolled,
  onButtonClick
}: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div 
        className="absolute inset-0 transition-transform duration-1000"
        style={{
          transform: scrolled ? 'scale(1.1)' : 'scale(1)',
        }}
      >
        <img
          src={heroData?.backgroundImage || Banner}
          alt="Hero Background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a4b7c]/95 via-[#2a4b7c]/85 to-[#2a4b7c]/80"></div>
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00c897]/20 via-transparent to-[#2a4b7c]/30 animate-gradient-shift"></div>
      </div>

      {/* Animated Morphing Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-[#00c897]/10 rounded-full blur-3xl animate-morph-1" style={{ top: '10%', left: '5%' }}></div>
        <div className="absolute w-80 h-80 bg-[#00c897]/15 rounded-full blur-3xl animate-morph-2" style={{ top: '50%', right: '10%' }}></div>
        <div className="absolute w-64 h-64 bg-[#00c897]/10 rounded-full blur-3xl animate-morph-3" style={{ bottom: '10%', left: '30%' }}></div>
      </div>

      {/* Animated Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-2 h-2 bg-[#00c897]/30 rounded-full animate-float-slow" style={{ top: '20%', left: '10%' }}></div>
        <div className="absolute w-3 h-3 bg-[#00c897]/20 rounded-full animate-float-medium" style={{ top: '60%', left: '15%' }}></div>
        <div className="absolute w-2 h-2 bg-[#00c897]/40 rounded-full animate-float-fast" style={{ top: '40%', right: '20%' }}></div>
        <div className="absolute w-4 h-4 bg-[#00c897]/15 rounded-full animate-float-slow" style={{ top: '70%', right: '10%' }}></div>
        <div className="absolute w-2 h-2 bg-[#00c897]/25 rounded-full animate-float-medium" style={{ top: '30%', left: '50%' }}></div>
        <div className="absolute w-3 h-3 bg-[#00c897]/30 rounded-full animate-float-fast" style={{ bottom: '20%', right: '30%' }}></div>
      </div>

      {/* Animated Wave at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg className="w-full h-24 animate-wave" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,0 C150,100 350,0 600,50 C850,100 1050,0 1200,50 L1200,120 L0,120 Z" fill="rgba(0,200,151,0.1)"></path>
        </svg>
        <svg className="w-full h-24 absolute bottom-0 animate-wave-slow" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,50 C200,0 400,100 600,50 C800,0 1000,100 1200,50 L1200,120 L0,120 Z" fill="rgba(0,200,151,0.05)"></path>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Title - Cân bằng khoảng cách trên/dưới chữ heading */}
            <div 
              className={`pt-4 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <h1 className="text-6xl md:text-7xl font-extrabold text-white leading-tight mb-4 animate-fade-in">
                <span 
                  className="inline-block animate-glow"
                  style={{ boxShadow: 'none !important' }}
                >
                  {heroData?.heading || 'StackWay'}
                </span>
              </h1>
              <h2 className="text-3xl md:text font-bold text-[#00c897] mb-6 animate-fade-in-delay">
                <span className="inline-block animate-text-shimmer bg-gradient-to-r from-[#00c897] via-white to-[#00c897] bg-clip-text text-transparent bg-[length:200%_auto]">
                  {heroData?.subheading || 'Giải pháp công nghệ toàn diện'}
                </span>
              </h2>
            </div>
            {heroData?.badge?.text && (
              <div 
                className={`inline-block px-4 py-2 bg-white/90 rounded-full transition-all duration-700 delay-100 backdrop-blur-sm border-none outline-none animate-badge-glow ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <span className="text-[#00c897] text-sm font-semibold">{heroData.badge.text}</span>
              </div>
            )}
            <p 
              className={`text-lg text-gray-200 leading-relaxed transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {heroData?.description || ''}
            </p>
            <div 
              className={`flex flex-col sm:flex-row gap-4 pt-4 transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              {heroData?.buttons
                ?.filter(btn => btn.isVisible)
                .sort((a, b) => a.order - b.order)
                .map((button) => (
                  button.actionType === 'url' ? (
                    <Link
                      key={button._id}
                      to={button.url}
                      className={`px-8 py-4 text-base font-semibold rounded-lg transition-all duration-300 whitespace-nowrap text-center transform hover:scale-105 hover:shadow-xl flex items-center justify-center ${
                        button.style === 'primary'
                          ? 'bg-[#00c897] text-white hover:bg-[#00b587] shadow-lg shadow-[#00c897]/30'
                          : 'border-2 border-[#00c897] text-[#00c897] hover:bg-white hover:text-[#333] hover:border-white'
                      }`}
                    >
                      {button.text}
                    </Link>
                  ) : (
                    <button
                      key={button._id}
                      onClick={() => onButtonClick(button)}
                      className={`px-8 py-4 text-base font-semibold rounded-lg transition-all duration-300 whitespace-nowrap transform hover:scale-105 hover:shadow-xl flex items-center justify-center ${
                        button.style === 'primary'
                          ? 'bg-[#00c897] text-white hover:bg-[#00b587] shadow-lg shadow-[#00c897]/30'
                          : 'border-2 border-[#00c897] text-[#00c897] hover:bg-white hover:text-[#333] hover:border-white'
                      }`}
                    >
                      {button.text}
                    </button>
                  )
                ))}
            </div>
            {/* Trust Points - No Numbers */}
            <div 
              className={`grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="group transform hover:scale-105 transition-transform duration-300">
                <div className="flex items-start gap-3 md:flex-col md:text-center md:items-center">
                  <div className="flex-shrink-0 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 md:mb-4 group-hover:bg-white/15 group-hover:border-[#00c897]/30 transition-all duration-300 shadow-lg">
                    <i className="ri-focus-3-line text-3xl text-[#00c897] drop-shadow-[0_0_8px_rgba(0,200,151,0.5)]"></i>
                  </div>
                  <div className="flex-1 md:flex-none">
                    <div className="text-base font-bold text-white mb-2">Đúng nghiệp vụ</div>
                    <div className="text-sm text-gray-300 leading-relaxed">Bắt đầu từ bài toán doanh nghiệp</div>
                  </div>
                </div>
              </div>
              <div className="group transform hover:scale-105 transition-transform duration-300">
                <div className="flex items-start gap-3 md:flex-col md:text-center md:items-center">
                  <div className="flex-shrink-0 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 md:mb-4 group-hover:bg-white/15 group-hover:border-[#00c897]/30 transition-all duration-300 shadow-lg">
                    <i className="ri-flow-chart text-3xl text-[#00c897] drop-shadow-[0_0_8px_rgba(0,200,151,0.5)]"></i>
                  </div>
                  <div className="flex-1 md:flex-none">
                    <div className="text-base font-bold text-white mb-2">Có hệ thống</div>
                    <div className="text-sm text-gray-300 leading-relaxed">Phân tích – triển khai – tối ưu</div>
                  </div>
                </div>
              </div>
              <div className="group transform hover:scale-105 transition-transform duration-300">
                <div className="flex items-start gap-3 md:flex-col md:text-center md:items-center">
                  <div className="flex-shrink-0 inline-flex items-center justify-center w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 md:mb-4 group-hover:bg-white/15 group-hover:border-[#00c897]/30 transition-all duration-300 shadow-lg">
                    <i className="ri-line-chart-line text-3xl text-[#00c897] drop-shadow-[0_0_8px_rgba(0,200,151,0.5)]"></i>
                  </div>
                  <div className="flex-1 md:flex-none">
                    <div className="text-base font-bold text-white mb-2">Đồng hành lâu dài</div>
                    <div className="text-sm text-gray-300 leading-relaxed">Vận hành hiệu quả, mở rộng lâu dài</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Floating Card with Animation */}
          <div 
            className={`relative hidden lg:block transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 animate-float-card hover:shadow-2xl hover:shadow-[#00c897]/20 transition-shadow duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-[#00c897] rounded-lg shadow-lg shadow-[#00c897]/50 animate-pulse-glow">
                  <i className="ri-rocket-line text-3xl text-white"></i>
                </div>
                <div>
                  <div className="font-bold text-white text-lg">Chúng tôi giúp</div>
                  <div className="font-bold text-white text-lg">doanh nghiệp triển khai</div>
                </div>

              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white transform hover:translate-x-2 transition-transform duration-300 group">
                  <i className="ri-shopping-cart-line text-[#00c897] text-xl group-hover:scale-125 transition-transform"></i>
                  <span className="text-sm font-medium">Giải pháp Ecommerce</span>
                </div>
                <div className="flex items-center gap-3 text-white transform hover:translate-x-2 transition-transform duration-300 group">
                  <i className="ri-database-2-line text-[#00c897] text-xl group-hover:scale-125 transition-transform"></i>
                  <span className="text-sm font-medium">Hệ thống ERP & CRM</span>
                </div>
                <div className="flex items-center gap-3 text-white transform hover:translate-x-2 transition-transform duration-300 group">
                  <i className="ri-cloud-line text-[#00c897] text-xl group-hover:scale-125 transition-transform"></i>
                  <span className="text-sm font-medium">Điện toán đám mây</span>
                </div>
                <div className="flex items-center gap-3 text-white transform hover:translate-x-2 transition-transform duration-300 group">
                  <i className="ri-robot-line text-[#00c897] text-xl group-hover:scale-125 transition-transform"></i>
                  <span className="text-sm font-medium">Process Automation với AI</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-scroll"></div>
        </div>
      </div>
    </section>
  );
}

